import React, { useState } from "react";
import { database } from "../firebaseConfig";
import { ref, push, get } from "firebase/database";
import "./WorldSettings.css";
import { standardizeInstagramProfile } from "../utils/InstagramHelper";

const WhisperUploader = ({ userId }) => {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [showMapping, setShowMapping] = useState(false);
  const [instagramField, setInstagramField] = useState("");
  const [message, setMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  // Function to generate a random number between min and max (inclusive)
  const getRandomNumber = (min, max) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  // Function to detect delimiter in a CSV line
  const detectDelimiter = (csvLine) => {
    // Commonly used delimiters and their priority
    const delimiters = [
      { char: ';', name: 'Semicolon' },
      { char: ',', name: 'Comma' }
    ];
    
    // Count occurrences of each delimiter
    const counts = delimiters.map(delimiter => ({
      ...delimiter,
      count: (csvLine.match(new RegExp(delimiter.char, 'g')) || []).length
    }));
    
    // Find the delimiter with most occurrences
    const maxDelimiter = counts.reduce((max, current) => 
      current.count > max.count ? current : max, { count: 0 });
    
    // If no delimiter was found or too few, use comma as default
    if (maxDelimiter.count <= 1) {
      return ',';
    }
    
    return maxDelimiter.char;
  };

  // Function to process the CSV file
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Check file extension
    if (!selectedFile.name.endsWith('.csv')) {
      setMessage("Please upload a CSV file.");
      return;
    }

    setFile(selectedFile);
    setMessage("File selected: " + selectedFile.name);

    // Read CSV file
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      
      // Split by lines
      const rows = text.split('\n');
      
      if (rows.length === 0) {
        setMessage("The CSV file is empty.");
        return;
      }
      
      // Detect delimiter using the first line
      const delimiter = detectDelimiter(rows[0]);
      console.log(`Detected delimiter: "${delimiter}"`);
      
      // Parse headers with detected delimiter
      const headers = rows[0].split(delimiter).map(header => header.trim());
      
      // Set headers for mapping
      setCsvHeaders(headers);
      
      // Parse data (without header row) with detected delimiter
      const parsedData = rows.slice(1)
        .filter(row => row.trim() !== '') // Skip empty lines
        .map(row => {
          const values = row.split(delimiter).map(value => value.trim());
          const rowObject = {};
          
          headers.forEach((header, index) => {
            rowObject[header] = values[index] || '';
          });
          
          return rowObject;
        });
      
      // Set the parsed data
      setCsvData(parsedData);
      
      // Show the mapping popup
      setShowMapping(true);
      
      // Inform the user about the detected delimiter
      const delimiterName = delimiter === ';' ? 'semicolon' : 'comma';
      setMessage(`CSV detected with ${delimiterName} as delimiter. ${parsedData.length} rows loaded.`);
    };
    
    reader.readAsText(selectedFile);
  };

  // Function to save data after mapping
  const handleSaveMapping = async () => {
    if (!instagramField) {
      setMessage("Please select a field for Instagram profiles.");
      return;
    }

    try {
      setIsUploading(true);
      setMessage("Uploading data...");

      // Load current WhisperNumber from settings
      const settingsRef = ref(database, `/Whisper/${userId}/db/settings/profileNumber`);
      const settingsSnapshot = await get(settingsRef);
      
      // Use 10 as default if no number is stored
      const maxAccountNumber = settingsSnapshot.exists() ? settingsSnapshot.val() : 10;
      
      // Prüfe zuerst die bestehenden Profile, um Duplikate zu vermeiden
      const profilesRef = ref(database, `/Whisper/${userId}/db/profiles`);
      const existingProfilesSnapshot = await get(profilesRef);
      
      // Sammle vorhandene Instagram-Profilnamen
      const existingProfiles = new Set();
      if (existingProfilesSnapshot.exists()) {
        existingProfilesSnapshot.forEach((profileSnapshot) => {
          const profile = profileSnapshot.val();
          if (profile.instagram_profile) {
            existingProfiles.add(profile.instagram_profile.toLowerCase());
          }
        });
      }
      
      // Filter und standardisiere Daten mit der ausgewählten Zuordnung
      // Entferne Duplikate innerhalb der CSV und im Vergleich zu den existierenden Profilen
      const processedUsernames = new Set();
      
      const mappedData = csvData
        .filter(row => row[instagramField])
        .map(row => {
          const standardized = standardizeInstagramProfile(row[instagramField]);
          
          // Generate a random account number between 1 and maxAccountNumber
          const accountNumber = getRandomNumber(1, Math.max(1, maxAccountNumber));
          
          return {
            username: standardized.username.toLowerCase(), // Für Duplikaterkennung
            data: {
              instagram_profile: standardized.username,
              profile_url: standardized.profile_url,
              import_date: new Date().toISOString(),
              source: "whisper_upload",
              raw_value: row[instagramField], // Also store the original value
              accountNumber: accountNumber, // New property: random account number
              status: "fresh" // New property: status always "fresh"
            }
          };
        })
        .filter(item => {
          // Prüfe, ob es ein gültiger Benutzername ist
          if (!item.username) return false;
          
          // Prüfe auf Duplikate in der aktuellen Batch
          if (processedUsernames.has(item.username)) return false;
          
          // Prüfe auf existierende Profile in der Datenbank
          if (existingProfiles.has(item.username)) return false;
          
          // Wenn alle Tests bestanden wurden, füge den Benutzernamen zum Set hinzu und behalte das Element
          processedUsernames.add(item.username);
          return true;
        })
        .map(item => item.data); // Für die Datenbank nur die tatsächlichen Daten extrahieren

      if (mappedData.length === 0) {
        setMessage("No valid Instagram profiles found.");
        setIsUploading(false);
        return;
      }

      // Upload to Firebase database
      const whisperRef = ref(database, `/Whisper/${userId}/db/profiles`);
      
      // Save each profile individually
      for (const profile of mappedData) {
        await push(whisperRef, profile);
      }

      // Report success
      setMessage(`${mappedData.length} Instagram profiles successfully imported!`);
      
      // Reset state
      setFile(null);
      setCsvData([]);
      setCsvHeaders([]);
      setShowMapping(false);
      setInstagramField("");
      
      // Reset file input
      document.getElementById('csv-file-input').value = '';
      
    } catch (error) {
      console.error("Error uploading data:", error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Function to cancel mapping
  const handleCancelMapping = () => {
    setShowMapping(false);
    setFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setInstagramField("");
    document.getElementById('csv-file-input').value = '';
    setMessage("");
  };

  return (
    <div className="whisperUploaderContainer">
      <h4>Import Instagram Profiles</h4>
      <p className="uploadDescription">
        Upload a CSV file with Instagram profiles. 
        After uploading, you can specify which column contains the Instagram profiles.
        CSV files with comma (,) or semicolon (;) as delimiters are supported.
      </p>

      <div className="uploadSection">
        <label htmlFor="csv-file-input" className="csvUploadLabel">
          Select CSV File
          <input
            id="csv-file-input"
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="csvFileInput"
            disabled={isUploading || showMapping}
          />
        </label>
        
        {message && <div className="uploadMessage">{message}</div>}
      </div>

      {/* Mapping Popup */}
      {showMapping && (
        <div className="mappingPopupOverlay">
          <div className="mappingPopup">
            <h3>Map Fields</h3>
            <p>Please select the column that contains Instagram profiles:</p>
            
            <div className="mappingField">
              <label>Instagram Profile Column:</label>
              <select
                value={instagramField}
                onChange={(e) => setInstagramField(e.target.value)}
                className="mappingSelect"
              >
                <option value="">-- Please select --</option>
                {csvHeaders.map((header, index) => (
                  <option key={index} value={header}>
                    {header}
                  </option>
                ))}
              </select>
            </div>

            {instagramField && (
              <div className="mappingPreview">
                <h4>Preview:</h4>
                <p>Selected column: <strong>{instagramField}</strong></p>
                <p>Sample values:</p>
                <ul className="previewList">
                  {csvData.slice(0, 3).map((row, index) => {
                    const value = row[instagramField] || '(empty)';
                    const standardized = value !== '(empty)' 
                      ? standardizeInstagramProfile(value) 
                      : { username: '', profile_url: '' };
                    
                    return (
                      <li key={index}>
                        Original: <strong>{value}</strong>
                        {value !== '(empty)' && (
                          <div className="previewStandardized">
                            Will be saved as: <strong>{standardized.username}</strong>
                            <br />
                            URL: <a href={standardized.profile_url} target="_blank" rel="noopener noreferrer">
                              {standardized.profile_url}
                            </a>
                            <br />
                            <span className="previewMetadata">
                              Status: <strong>fresh</strong>, 
                              Account Number: <strong>random 1-X</strong>
                            </span>
                          </div>
                        )}
                      </li>
                    );
                  })}
                  {csvData.length > 3 && <li>...</li>}
                </ul>
                <p>
                  Found a total of {csvData.filter(row => {
                    const value = row[instagramField];
                    if (!value) return false;
                    const standardized = standardizeInstagramProfile(value);
                    return standardized.username;
                  }).length} valid Instagram profiles.
                </p>
                <div className="importInfoBox">
                  <p>
                    Each profile will automatically receive:
                  </p>
                  <ul>
                    <li>A status of <strong>"fresh"</strong></li>
                    <li>A random account number between <strong>1</strong> and the configured <strong>Profile Number</strong></li>
                  </ul>
                </div>
              </div>
            )}
            
            <div className="mappingButtons">
              <button 
                className="cancelMappingButton" 
                onClick={handleCancelMapping}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button 
                className="saveMappingButton" 
                onClick={handleSaveMapping}
                disabled={!instagramField || isUploading}
              >
                {isUploading ? "Importing..." : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhisperUploader; 